export const FLOATING_LAYER_SELECTOR = [
  '[data-slot="dialog-overlay"]',
  '[data-slot="dialog-content"]',
  '[data-slot="sheet-overlay"]',
  '[data-slot="sheet-content"]',
  '[data-slot="dropdown-menu-content"]',
  '[data-slot="dropdown-menu-sub-content"]',
  '[data-slot="context-menu-content"]',
  '[data-slot="popover-content"]',
  '[data-slot="select-content"]',
  '[data-slot="tooltip-content"]',
  // The toaster list is only a positioning box. Its cards are position:absolute
  // and paint outside that box, so the list's rect never covers the browser.
  '[data-sonner-toast][data-mounted="true"]:not([data-visible="false"])',
  '[role="dialog"]',
  '[role="menu"]',
  '[role="listbox"]',
  '[role="tooltip"]',
].join(',');

export interface FloatingLayerObserverOptions {
  /** Portal host containing trusted DOM overlays. Defaults to document.body. */
  portalHost?: HTMLElement;
}

/**
 * Observe whether a trusted DOM floating layer visually overlaps `target`.
 *
 * Electron WebContentsView is composited above the renderer DOM, so callers
 * use this signal to replace the native view with a screenshot while menus,
 * dialogs, tooltips, or notifications cross the native content rectangle.
 */
export function observeFloatingLayerOverlap(
  target: HTMLElement,
  onChange: (overlapping: boolean) => void,
  options: FloatingLayerObserverOptions = {},
): () => void {
  const doc = target.ownerDocument;
  const portalHost = options.portalHost ?? doc.body;
  let disposed = false;
  let animationFrame = 0;
  let previous: boolean | undefined;
  const evaluate = (): void => {
    if (disposed) {
      return;
    }
    const layers = Array.from(
      portalHost.querySelectorAll<HTMLElement>(FLOATING_LAYER_SELECTOR),
    );
    layerResizeObserver?.disconnect();
    for (const layer of layers) {
      layerResizeObserver?.observe(layer);
    }
    const overlapping = layers.some((layer) =>
      floatingLayerOverlapsTarget(layer, target),
    );
    if (overlapping !== previous) {
      previous = overlapping;
      onChange(overlapping);
    }
  };
  const schedule = (): void => {
    if (disposed || animationFrame !== 0) {
      return;
    }
    animationFrame = requestAnimationFrame(() => {
      animationFrame = 0;
      if (disposed) {
        return;
      }
      evaluate();
    });
  };

  // Child insertion happens in the same renderer turn as a floating layer's
  // first paint. Evaluate that mutation in its microtask instead of adding a
  // frame of native-view overlap; scrolling and animations remain rAF-batched.
  const mutationObserver = new MutationObserver(evaluate);
  const layerResizeObserver =
    typeof ResizeObserver === 'undefined'
      ? undefined
      : new ResizeObserver(schedule);
  mutationObserver.observe(portalHost, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: [
      'class',
      'style',
      'hidden',
      'open',
      'data-open',
      'data-closed',
      'data-state',
    ],
  });
  const targetResizeObserver =
    typeof ResizeObserver === 'undefined'
      ? undefined
      : new ResizeObserver(schedule);
  targetResizeObserver?.observe(target);
  window.addEventListener('resize', schedule);
  window.addEventListener('scroll', schedule, true);
  doc.addEventListener('transitionrun', schedule, true);
  doc.addEventListener('transitionend', schedule, true);
  doc.addEventListener('animationstart', schedule, true);
  doc.addEventListener('animationend', schedule, true);
  schedule();

  return () => {
    disposed = true;
    if (animationFrame !== 0) {
      cancelAnimationFrame(animationFrame);
    }
    mutationObserver.disconnect();
    targetResizeObserver?.disconnect();
    layerResizeObserver?.disconnect();
    window.removeEventListener('resize', schedule);
    window.removeEventListener('scroll', schedule, true);
    doc.removeEventListener('transitionrun', schedule, true);
    doc.removeEventListener('transitionend', schedule, true);
    doc.removeEventListener('animationstart', schedule, true);
    doc.removeEventListener('animationend', schedule, true);
  };
}

export function floatingLayerOverlapsTarget(
  layer: HTMLElement,
  target: HTMLElement,
): boolean {
  if (layer.hidden || !layer.isConnected) {
    return false;
  }
  // A native-content target hosted inside a dialog is part of that dialog, not
  // obscured by it. Only sibling/descendant floating layers can cover it.
  if (layer.contains(target)) {
    return false;
  }
  const style = getComputedStyle(layer);
  if (
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    Number(style.opacity) === 0
  ) {
    return false;
  }
  const targetRect = target.getBoundingClientRect();
  const layerRect = layer.getBoundingClientRect();
  const left = Math.max(targetRect.left, layerRect.left);
  const top = Math.max(targetRect.top, layerRect.top);
  const right = Math.min(targetRect.right, layerRect.right);
  const bottom = Math.min(targetRect.bottom, layerRect.bottom);
  if (right <= left || bottom <= top) {
    return false;
  }
  if (layer.dataset.slot?.endsWith('-overlay')) {
    return true;
  }
  const samplePoints: readonly [number, number][] = [
    [(left + right) / 2, (top + bottom) / 2],
    [left + 1, top + 1],
    [right - 1, top + 1],
    [left + 1, bottom - 1],
    [right - 1, bottom - 1],
  ];
  return samplePoints.some(([x, y]) => {
    const hit = target.ownerDocument.elementFromPoint(x, y);
    return hit !== null && (hit === layer || layer.contains(hit));
  });
}
